<template>
  <div class="billing-plans-page">
    <b-card
      no-body
      class="current-plan-card"
    >
      <b-card-header class="current-plan-header">
        <h3>Current plan</h3>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col
            lg="6"
            class="mb-2 mb-lg-0"
          >
            <div class="plan-title">
              Your Current Plan is <strong>{{ currentPlan.name }}</strong>
            </div>
            <p class="plan-subtext">
              {{ currentPlan.description }}
            </p>

            <div class="plan-title mt-2">
              Active until {{ currentPlan.activeUntil }}
            </div>
            <p class="plan-subtext">
              We will send you a notification upon Subscription expiration
            </p>

            <div class="plan-price-row mt-2">
              <span class="plan-price">${{ currentPlan.price }} Per Month</span>
              <span class="popular-tag">Popular</span>
            </div>
            <p class="plan-subtext">
              Standard plan for small to medium businesses
            </p>

            <div class="plan-actions mt-2">
              <b-button
                class="mr-1 upgrade-plan-btn"
                @click="goToUpgrade"
              >
                Upgrade Plan
              </b-button>
              <b-button
                variant="outline-secondary"
                :disabled="billingPortalLoading"
                @click="openBillingPortal"
              >
                <b-spinner v-if="billingPortalLoading" small class="mr-50" />
                Manage payment methods
              </b-button>
            </div>
          </b-col>

          <b-col lg="6">
            <div class="attention-banner">
              <div class="plan-warning">
                We need your attention!
              </div>
              <div class="plan-warning-sub">
                your plan requires update
              </div>
            </div>

            <div class="days-head mt-2">
              <span>Days</span>
              <span>{{ currentPlan.usedDays }} of {{ currentPlan.totalDays }} Days</span>
            </div>

            <b-progress
              :max="currentPlan.totalDays"
              height="10px"
              class="days-progress"
            >
              <b-progress-bar
                class="days-progress-fill"
                :value="currentPlan.usedDays"
              />
            </b-progress>

            <p class="days-subtext">
              {{ currentPlan.totalDays - currentPlan.usedDays }} days remaining until your plan requires update
            </p>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card
      no-body
      class="billing-history-card mt-2"
    >
      <b-card-header class="billing-history-header">
        <h3>Billing History</h3>
        <div class="billing-tools">
          <b-form-input
            v-model="search"
            placeholder="Search"
            class="billing-search"
          />
          <b-dropdown
            right
            variant="outline-secondary"
            size="sm"
            text="Export"
          >
            <b-dropdown-item @click="exportBillingCsv">
              CSV
            </b-dropdown-item>
            <b-dropdown-item @click="exportBillingCsv">
              Excel
            </b-dropdown-item>
            <b-dropdown-item disabled>
              PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-card-header>

      <b-table
        responsive
        class="billing-table mb-0"
        :items="paginatedRows"
        :fields="tableFields"
        show-empty
        empty-text="No billing records found"
      >
        <template #cell(status)="{ item }">
          <b-badge
            pill
            variant="light-success"
            class="status-paid"
          >
            {{ item.status }}
          </b-badge>
        </template>

        <template #cell(actions)="{ item }">
          <div class="action-icons">
            <feather-icon
              icon="DownloadIcon"
              size="15"
              class="cursor-pointer"
              title="Download invoice"
              @click="exportBillingCsv"
            />
            <feather-icon
              icon="SendIcon"
              size="15"
              class="cursor-pointer"
              @click="emailInvoice(item)"
            />
            <feather-icon
              icon="EyeIcon"
              size="15"
              class="cursor-pointer"
              @click="viewInvoice(item)"
            />
          </div>
        </template>
      </b-table>

      <div class="billing-footer">
        <small>Showing {{ paginationStart }} to {{ paginationEnd }} of {{ filteredRows.length }} entries</small>

        <b-pagination
          v-model="currentPage"
          :total-rows="filteredRows.length"
          :per-page="perPage"
          first-number
          last-number
          size="sm"
          class="mb-0"
        >
          <template #prev-text>
            Previous
          </template>
          <template #next-text>
            Next
          </template>
        </b-pagination>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BBadge,
  BProgress,
  BProgressBar,
  BTable,
  BFormInput,
  BPagination,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import { downloadCsv } from '@/utils/exportCsv'
import { notifyInfo } from '@/utils/uiActions'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BBadge,
    BProgress,
    BProgressBar,
    BTable,
    BFormInput,
    BPagination,
    BDropdown,
    BDropdownItem,
  },
  data() {
    return {
      search: '',
      currentPage: 1,
      perPage: 8,
      currentPlan: {
        name: 'Basic',
        description: 'A simple start for everyone',
        activeUntil: 'Dec 09, 2025',
        price: 99,
        usedDays: 4,
        totalDays: 30,
      },
      tableFields: [
        { key: 'no', label: 'NO' },
        { key: 'transactionId', label: 'TRANSACTION ID' },
        { key: 'status', label: 'STATUS' },
        { key: 'issuedDate', label: 'ISSUED DATE' },
        { key: 'dueDate', label: 'DUE DATE' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      isLoading: false,
      loadError: '',
      billingPortalLoading: false,
      rows: [],
    }
  },
  computed: {
    filteredRows() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.rows
      return this.rows.filter(item => `${item.no} ${item.transactionId} ${item.status} ${item.issuedDate} ${item.dueDate}`.toLowerCase().includes(term))
    },
    paginatedRows() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredRows.slice(start, start + this.perPage)
    },
    paginationStart() {
      if (!this.filteredRows.length) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    paginationEnd() {
      if (!this.filteredRows.length) return 0
      return Math.min(this.currentPage * this.perPage, this.filteredRows.length)
    },
  },
  watch: {
    search() {
      this.currentPage = 1
    },
  },
  mounted() {
    this.fetchBillingData()
  },
  methods: {
    goToUpgrade() {
      this.$router.push({ name: 'billing-details' }).catch(() => {})
    },
    async openBillingPortal() {
      this.billingPortalLoading = true
      try {
        const { data } = await vendorPanelApi.createBillingPortalSession()
        const portalUrl = data?.data?.portal_url
        if (portalUrl) {
          window.location.href = portalUrl
          return
        }
        notifyInfo(this, 'Billing portal URL was not returned.')
      } catch (error) {
        notifyInfo(this, error?.response?.data?.message || 'Unable to open billing portal.')
      } finally {
        this.billingPortalLoading = false
      }
    },
    exportBillingCsv() {
      if (!this.filteredRows.length) return
      downloadCsv('billing-history.csv', this.filteredRows, this.tableFields)
    },
    viewInvoice(item) {
      if (item?.invoiceUrl) {
        window.open(item.invoiceUrl, '_blank', 'noopener,noreferrer')
        return
      }
      this.$swal({
        title: 'Invoice details',
        html: `
          <p><strong>Transaction:</strong> ${item.transactionId}</p>
          <p><strong>Status:</strong> ${item.status}</p>
          <p><strong>Issued:</strong> ${item.issuedDate}</p>
          <p><strong>Due:</strong> ${item.dueDate}</p>
        `,
        confirmButtonText: 'Close',
      })
    },
    emailInvoice(item) {
      notifyInfo(this, `Invoice ${item.transactionId} will be sent to your registered email.`)
    },
    async fetchBillingData() {
      this.isLoading = true
      this.loadError = ''
      try {
        const [plansRes, walletRes] = await Promise.all([
          vendorPanelApi.plans({}),
          vendorPanelApi.walletHistory({ page: 1, per_page: 50 }),
        ])
        const planData = plansRes?.data?.data || {}
        const currentPlanData = planData.current_plan
          || (planData.plans || []).find(item => item.is_current)
        if (currentPlanData) {
          this.currentPlan = {
            name: currentPlanData.name || this.currentPlan.name,
            description: currentPlanData.description || this.currentPlan.description,
            activeUntil: currentPlanData.active_until || currentPlanData.activeUntil || this.currentPlan.activeUntil,
            price: currentPlanData.price ?? this.currentPlan.price,
            usedDays: currentPlanData.used_days ?? currentPlanData.usedDays ?? this.currentPlan.usedDays,
            totalDays: currentPlanData.total_days ?? currentPlanData.totalDays ?? this.currentPlan.totalDays,
          }
        }
        const walletPayload = walletRes?.data?.data || {}
        const history = walletPayload.data || []
        const mapped = (Array.isArray(history) ? history : []).map((item, index) => ({
          no: String(index + 1).padStart(2, '0'),
          transactionId: item.transaction_id || item.id || '-',
          status: item.status || 'Paid',
          issuedDate: item.date || item.issued_date || item.created_at || '-',
          dueDate: item.expire_date || item.due_date || item.paid_at || '-',
          invoiceUrl: item.invoice_url || '',
        }))
        this.rows = mapped
      } catch (error) {
        this.loadError = error.response?.data?.message || ''
        this.rows = []
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.billing-plans-page {
  .current-plan-card,
  .billing-history-card {
    border: 1px solid #ececf3;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(39, 44, 62, 0.05);
  }

  .current-plan-header,
  .billing-history-header {
    border-bottom: 1px solid #ececf3;
    padding: 0.95rem 1.1rem;

    h3 {
      margin: 0;
      color: #4b465c;
      font-size: 1.75rem;
      font-weight: 700;
    }
  }

  .current-plan-card .card-body {
    padding: 1.3rem 1.1rem 1.2rem;
  }

  .plan-title {
    color: #4b465c;
    font-size: 1.02rem;
    font-weight: 500;
  }

  .plan-subtext {
    color: #7f7a93;
    margin-bottom: 0;
    font-size: 0.95rem;
  }

  .plan-price-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
  }

  .plan-price {
    color: #4b465c;
    font-size: 1.9rem;
    font-weight: 700;
    line-height: 1.1;
  }

  .attention-banner {
    margin-bottom: 1.1rem;

    .plan-warning {
      background: transparent;
      color: #e48b2a;
      border-radius: 0;
      padding: 0 0 0.25rem;
      font-weight: 700;
      font-size: 0.98rem;
    }

    .plan-warning-sub {
      background: transparent;
      color: #d0934b;
      border-radius: 0;
      padding: 0;
      margin-bottom: 0;
      font-size: 0.95rem;
    }
  }

  .days-head {
    display: flex;
    justify-content: space-between;
    color: #011425;
    font-weight: 600;
    font-size: 1.03rem;
    margin-bottom: 0.45rem;
  }

  .days-progress {
    background: #e9e7fd;
    border-radius: 999px;

    ::v-deep .days-progress-fill {
      background: #011425;
      border-radius: 999px;
    }
  }

  .days-subtext {
    margin-top: 0.45rem;
    color: #7f7a93;
    font-size: 0.95rem;
    margin-bottom: 0;
  }

  .upgrade-plan-btn {
    background: #011425;
    border-color: #011425;

    &:hover,
    &:focus,
    &:active {
      background: #011425 !important;
      border-color: #011425 !important;
    }
  }

  .billing-history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .billing-tools {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  .billing-search {
    width: 170px;
    height: 34px;
  }

  .billing-table {
    ::v-deep thead th {
      font-size: 0.8rem;
      font-weight: 700;
      color: #6f6b86;
      border-top: 0;
      background: #fff;
      padding-top: 0.8rem;
      padding-bottom: 0.8rem;
    }

    ::v-deep tbody td {
      vertical-align: middle;
      color: #5e5873;
      font-size: 0.9rem;
      padding-top: 0.85rem;
      padding-bottom: 0.85rem;
    }
  }

  .status-paid {
    color: #28c76f;
    background: rgba(40, 199, 111, 0.15);
    font-size: 0.72rem;
    font-weight: 700;
  }

  .action-icons {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    color: #6f6b86;
  }

  .billing-footer {
    padding: 0.85rem 1rem 0.95rem;
  }
}
</style>
